import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import APLogo from '@alphapoint/apex-web/lib/components/common/APLogo/APLogo';
import PageHeaderNavComponent from '../../components/PageHeader/PageHeaderNavComponent';
import UserSummaryContainer from '../../components/UserSummary/UserSummaryContainer';
import './PageHeaderLayout.css';
import './UserSummaryOverrides.css';
import Link from '../../components/common/Link';
import { ThemeToggler } from '@alphapoint/apex-web/lib/components/Theme/ThemeToggler/ThemeToggler';
import LanguageSelectorContainer from '@alphapoint/apex-web/lib/components/Language/LanguageSelectorContainer';
import APButton from '@alphapoint/apex-web/lib/components/common/APButton';
import config from '../../config';
import {
  marginSystemEnabledSelector,
  marginBorrowerEnabledSelector,
  isMarginActiveSelector,
} from '@alphapoint/apex-web/lib/redux/margin/selectors/marginSelectors';
import {
  futuresEnabledSelector,
  futuresSystemEnabledSelector,
  isFuturesActiveSelector,
} from '@alphapoint/apex-web/lib/redux/futures/selectors/futuresSelectors';
import APIcon from '@alphapoint/apex-web/lib/components/common/APIcon';
import MobileNavigationComponent from '../../components/MobileNavigation/MobileNavigationComponent';

const pageHeaderClasses = getBEMClasses('page-header');

// Enum-like constants for active feature types
const FEATURES = {
  MARGIN: 'Margin System',
  FUTURES: 'Futures System',
  NONE: 'Spot System',
};

const PageHeaderComponent = ({
  isAuthenticated,
  systemMarginEnabled,
  marginBorrowerEnabled,
  systemFuturesEnabled,
  futuresEnabled,
  activeFeature,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Filter Trade menu options based on systemMarginEnabled and systemFuturesEnabled
  const mainNavItems = useMemo(() => {
    return config.Navigation.menuNavigationItems.map((item) => {
      if (item.text === 'Trade' && Array.isArray(item.submenuNavItems)) {
        const filteredSubmenu = item.submenuNavItems.filter((sub) => {
          if (sub.text === 'Margin') return systemMarginEnabled;
          if (sub.text === 'Futures') return systemFuturesEnabled;
          return true; // spot, buy-sell, stream, etc.
        });
        return { ...item, submenuNavItems: filteredSubmenu };
      }
      return item;
    });
  }, [systemMarginEnabled, systemFuturesEnabled]);

  // Mobile nav items include auth-specific items when authenticated
  const mobileNavItems = useMemo(() => {
    const items = [...mainNavItems];

    if (isAuthenticated) {
      items.push(
        {
          text: 'Settings',
          path: '/settings/user',
        },
        {
          text: 'Sign Out',
          path: '#',
          isSignOut: true,
        },
      );
    } else {
      items.push({
        text: 'Login',
        path: '/login',
      });
    }

    return items;
  }, [mainNavItems, isAuthenticated]);

  return (
    <React.Fragment>
      <div className={pageHeaderClasses('container')}>
        <div className={pageHeaderClasses('content-wrapper')}>
          <div className={pageHeaderClasses('header-item', 'logo')}>
            <APLogo customClass={pageHeaderClasses('logo')} linkTo="/" />
            {isAuthenticated && location.pathname !== '/' && activeFeature && (
              <span className={pageHeaderClasses('feature-label')}>{t(activeFeature, { ns: 'core' })}</span>
            )}
          </div>
          <PageHeaderNavComponent navItems={mainNavItems} />
          <div className={pageHeaderClasses('right-panel')}>
            {isAuthenticated &&
              ((systemMarginEnabled && !marginBorrowerEnabled) || (systemFuturesEnabled && !futuresEnabled)) && (
                <APButton
                  onClick={() => history.push('/settings/trade')}
                  classModifiers={pageHeaderClasses('enable-margin')}
                  type="button">
                  {t('Enable Leveraged Trading', { ns: 'core' })}
                </APButton>
              )}
            <ThemeToggler />
            <LanguageSelectorContainer />
            {isAuthenticated ? (
              <UserSummaryContainer customClass="page-header-user-summary" settingsRoute="/settings/user" />
            ) : (
              <div className={pageHeaderClasses('login-content-wrapper')}>
                <Link href={'/login'} className={pageHeaderClasses('login-link')}>
                  <span className="text">{t('Login')}</span>
                  <span className="icon">
                    <svg width="19" height="7" viewBox="0 0 19 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14.4049 3.78168V6.5403L18.2874 3.32191L14.4049 0.103516V2.86214H0.816089V3.78168H14.4049Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={pageHeaderClasses('mobile-content')}>
        <div
          className={pageHeaderClasses('mobile-content-item', 'left-panel', isMobileMenuOpen ? 'active' : '')}
          onClick={handleMobileMenuToggle}>
          <APIcon name="menu-toggle" />
        </div>
        <div className={pageHeaderClasses('mobile-content-item', 'logo')}>
          <APLogo customClass={pageHeaderClasses('logo')} linkTo="/" />
          {isAuthenticated && location.pathname !== '/' && activeFeature && (
            <span className={pageHeaderClasses('feature-label')}>{t(activeFeature, { ns: 'core' })}</span>
          )}
        </div>
        <div className={pageHeaderClasses('mobile-content-item', 'right-panel')}>
          <ThemeToggler />
          <LanguageSelectorContainer />
        </div>
      </div>

      {/* Mobile Navigation Component */}
      <MobileNavigationComponent
        isOpen={isMobileMenuOpen}
        onClose={handleMobileNavClick}
        navItems={mobileNavItems}
        isAuthenticated={isAuthenticated}
        systemMarginEnabled={systemMarginEnabled}
        marginBorrowerEnabled={marginBorrowerEnabled}
        systemFuturesEnabled={systemFuturesEnabled}
        futuresEnabled={futuresEnabled}
      />
    </React.Fragment>
  );
};

const mapStateToProps = ({ auth: { isAuthenticated }, ...state }) => {
  const isMarginActive = isMarginActiveSelector(state);
  const isFuturesActive = isFuturesActiveSelector(state);

  let activeFeature = FEATURES.NONE;
  if (isMarginActive) {
    activeFeature = FEATURES.MARGIN;
  } else if (isFuturesActive) {
    activeFeature = FEATURES.FUTURES;
  }

  return {
    isAuthenticated,
    systemMarginEnabled: marginSystemEnabledSelector(state),
    marginBorrowerEnabled: marginBorrowerEnabledSelector(state),
    systemFuturesEnabled: futuresSystemEnabledSelector(state),
    futuresEnabled: futuresEnabledSelector(state),
    activeFeature,
  };
};

export default withRouter(connect(mapStateToProps)(PageHeaderComponent));
